"use client";

import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { PortableText } from "@portabletext/react";
import { stegaClean } from "@sanity/client/stega";
import { AnimatePresence, motion, type MotionProps } from "framer-motion";

import Chip from "@/components/global/ui/Chip";
import DecorationLine from "@/components/global/ui/DecorationLine";
import DotsBackdrop from "@/components/lp-long/DotsBackdrop";
import { HLSVideoPlayer } from "@/components/ui/SanityVideoPlayer";
import { cn } from "@/lib/utils";
import { richTextComponent } from "@/sanity/sanityPortableText";
import { CTACornerButton } from "@/shadcn/ui/corner-button";
import { PageLPLongSchema } from "@/sanity/pages/PageLPLongTemplate";

import chevron from "@/public/lp-long/methode-chevron.svg";
import flag from "@/public/lp-long/methode-flag.svg";
import noise from "@/public/lp-long/methode-noise.png";

// framer's div typing clashes with the installed React types on className; keep the cast local
const MotionDiv = motion.div as React.ComponentType<
  React.HTMLAttributes<HTMLDivElement> & MotionProps & { key?: React.Key }
>;

const EASE = [0.22, 1, 0.36, 1] as const;

// accent colors from Figma, cycled when an item has no explicit color
const ACCENT_COLORS = ["#F9E0B9", "#D9F9B9", "#E9B9F9", "#B9F0F9", "#B9C0F9"];

type Item = PageLPLongSchema["methodeItems"][number];

/** Media of one item: mux video with the image as poster, or the image alone. */
function Media({ item, className }: { item: Item; className?: string }) {
  const isVideo = stegaClean(item?.mediaType) === "video";
  const hasVideo = item?.video?.muxVideo?.playbackId || item?.video?.embedUrl;

  return (
    <div className={cn("relative overflow-clip bg-[#101010]", className)}>
      {isVideo && hasVideo ? (
        <HLSVideoPlayer
          source={item.video}
          autoPlay
          muted
          loop
          playsInline
          poster={item?.image?.src}
          className="absolute inset-0 !h-full !w-full object-cover"
        />
      ) : (
        item?.image?.src && (
          <Image
            className="absolute inset-0 !h-full !w-full object-cover"
            src={item.image}
            alt=""
          />
        )
      )}
      {/* grain, like the Figma "Noise & Texture" layer */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[.16] mix-blend-soft-light"
        style={{ backgroundImage: `url(${noise.src})`, backgroundSize: "60rem 60rem" }}
        aria-hidden
      />
    </div>
  );
}

export default function MethodeSection({ data, className }: { data: PageLPLongSchema; className?: string }) {
  const items = data?.methodeItems ?? [];
  const hasItems = items.length > 0;
  const [active, setActive] = useState(0);
  const current = items[active];
  const userPicked = useRef(false);

  // on desktop, the header, the accordion and the media panel are sticky; a separate track
  // of equal-height steps (not the accordion itself, whose own height changes with which
  // item is open) provides the scroll runway, so measuring against it can't feed back
  // into the thing it's measuring.
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if (window.matchMedia("(max-width: 1023px)").matches) return;

    const TRIGGER_FRACTION = 0.4;

    const pickActive = () => {
      if (userPicked.current) return;
      const triggerY = window.innerHeight * TRIGGER_FRACTION;

      let bestIndex = 0;
      let bestDistance = -Infinity;
      stepRefs.current.forEach((el, index) => {
        if (!el) return;
        const top = el.getBoundingClientRect().top;
        if (top <= triggerY && top > bestDistance) {
          bestDistance = top;
          bestIndex = index;
        }
      });
      setActive(bestIndex);
    };

    pickActive();
    window.addEventListener("scroll", pickActive, { passive: true });
    window.addEventListener("resize", pickActive);
    return () => {
      window.removeEventListener("scroll", pickActive);
      window.removeEventListener("resize", pickActive);
    };
  }, [items.length]);

  // a manual click wins over the observer for one tick, then scroll takes back over
  useEffect(() => {
    if (!userPicked.current) return;
    const release = () => {
      userPicked.current = false;
    };
    window.addEventListener("wheel", release, { once: true, passive: true });
    window.addEventListener("touchmove", release, { once: true, passive: true });
    return () => {
      window.removeEventListener("wheel", release);
      window.removeEventListener("touchmove", release);
    };
  }, [active]);

  return (
    <section
      id="methode"
      className={cn(
        "relative overflow-clip bg-[#0A1A1D] pt-[8rem] pb-[8rem] lg:pt-[15rem] lg:pb-[13.75rem]",
        className,
      )}>
      <DotsBackdrop light />
      <DecorationLine
        className="hidden lg:left-[8.85%] lg:block"
        orientation="vertical"
        position="left"
        style="light"
      />
      <DecorationLine
        className="hidden lg:right-[8.85%] lg:block"
        orientation="vertical"
        position="right"
        style="light"
      />

      <div className="relative container flex flex-col items-center">
        {/* the relative wrapper is tall (runway) so there's room to scroll through; the
            header and the row of accordion + media stick near the top of it together */}
        <div
          className={cn("relative flex w-full flex-col items-center", hasItems && "lg:min-h-[var(--methode-runway)]")}
          style={{ "--methode-runway": `${items.length * 100 + 20}vh` } as React.CSSProperties}>
          {/* invisible, evenly spaced steps: measuring against these (not the
              accordion, whose own height changes with which item is open) is what
              keeps the active index from feeding back into its own measurement */}
          {hasItems && (
            <div
              className="pointer-events-none absolute inset-0 hidden lg:block"
              aria-hidden>
              {items.map((_, i) => (
                <div
                  key={i}
                  ref={(el) => {
                    stepRefs.current[i] = el;
                  }}
                  // the first step is taller so the section eases into the scroll-driven
                  // switching instead of jumping off item 1 on the very first scroll tick
                  className={i === 0 ? "h-[120vh]" : "h-[100vh]"}
                />
              ))}
            </div>
          )}

          <div className={cn("flex w-full flex-col items-center", hasItems && "lg:sticky lg:top-[10rem]")}>
            {/* header */}
            <Chip
              className="mb-[2.375rem] prose-p:font-geistmono prose-p:text-[#202223] prose-p:uppercase"
              image={data?.methodeChip?.icon}
              text={data?.methodeChip?.text}
            />

            {data?.methodeTitle && (
              <div className="richtext px-[2rem] lg:px-0 prose-em:font-ivy prose-em:text-[#D9F9B9] prose-headings:mb-0 prose-headings:w-[min(122.375rem,100%)] prose-headings:text-[4rem] prose-headings:leading-none prose-headings:text-white lg:prose-headings:text-[6.5rem]">
                <PortableText
                  value={data.methodeTitle}
                  components={richTextComponent}
                />
              </div>
            )}

            {data?.methodeText && (
              <div className="richtext mt-[2.375rem] px-[2rem] lg:px-0 prose-p:mb-0 prose-p:w-[min(105.5rem,100%)] prose-p:text-[2.25rem] prose-p:leading-[1.5] prose-p:text-white/60">
                <PortableText
                  value={data.methodeText}
                  components={richTextComponent}
                />
              </div>
            )}

            {/* accordion + media */}
            {hasItems && (
              <div className="mt-[7rem] flex w-[min(150rem,100%)] items-start gap-[3.5rem]">
                <ul
                  className="flex min-w-0 flex-1 flex-col gap-[1.5rem]"
                  role="tablist">
                  {items.map((item, i) => {
                    const accent = item?.color || ACCENT_COLORS[i % ACCENT_COLORS.length];
                    const open = i === active;
                    return (
                      <li
                        key={i}
                        className="overflow-clip rounded-[1rem] bg-white/5">
                        {/* below lg the section is static: every item is open and the header
                            is inert, so nothing on mobile reads `active` */}
                        <button
                          type="button"
                          role="tab"
                          aria-selected={open}
                          aria-expanded={open}
                          onClick={() => {
                            userPicked.current = true;
                            setActive(i);
                          }}
                          className="pointer-events-none flex w-full cursor-default items-center justify-between gap-[2rem] p-[3rem] text-left lg:pointer-events-auto lg:cursor-pointer">
                          <span className="flex items-center gap-[2rem]">
                            <span
                              className="rounded-full px-[1.5rem] py-[.25rem] font-geist text-[1.5rem] leading-[1.75] font-medium tracking-[-0.06em] text-black"
                              style={{ backgroundColor: accent }}>
                              {String(i + 1).padStart(2, "0")}
                            </span>
                            <span className="font-geist text-[2.5rem] leading-none font-medium tracking-[-0.036em] text-white lg:whitespace-nowrap">
                              {item?.title}
                            </span>
                          </span>
                          <Image
                            className={cn(
                              "hidden !w-[1.75rem] shrink-0 transition-transform duration-300 lg:block",
                              open && "rotate-180",
                            )}
                            src={chevron}
                            alt=""
                          />
                        </button>

                        {/* body — height animates via the grid-rows trick */}
                        <div
                          className={cn(
                            "grid transition-[grid-template-rows] duration-300 ease-[cubic-bezier(.22,1,.36,1)]",
                            "grid-rows-[1fr]",
                            open ? "lg:grid-rows-[1fr]" : "lg:grid-rows-[0fr]",
                          )}>
                          <div
                            className={cn(
                              "min-h-0 translate-y-0 overflow-hidden opacity-100 transition-[opacity,transform] duration-[250ms] ease-[cubic-bezier(.22,1,.36,1)]",
                              open
                                ? "lg:translate-y-0 lg:opacity-100 lg:delay-[50ms]"
                                : "lg:-translate-y-[12px] lg:opacity-0 lg:delay-0",
                            )}>
                            {/* mobile: each item carries its own media */}
                            <div className="px-[2rem]">
                              <Media
                                item={item}
                                className="h-[50.5rem] w-full rounded-[.4rem] lg:hidden lg:h-[54.375rem]"
                              />
                            </div>

                            <div className="flex flex-col gap-[2.5rem] px-[3rem] py-[4rem] lg:py-[2.5rem]">
                              {item?.text && (
                                <p className="mb-0 text-[2.25rem] leading-[1.4] text-white/60 [text-box-edge:cap_alphabetic] [text-box-trim:trim-both]">
                                  {item.text}
                                </p>
                              )}
                              <div
                                className="h-px w-full bg-white/10"
                                aria-hidden
                              />
                              <div className="flex flex-col gap-[3rem]">
                                {item?.listLabel && (
                                  <p className="mb-0 font-geistmono text-[2rem] leading-[1.4] text-white/50 uppercase [text-box-edge:cap_alphabetic] [text-box-trim:trim-both]">
                                    {item.listLabel}
                                  </p>
                                )}
                                {!!item?.list?.length && (
                                  <ul className="flex flex-col gap-[3rem]">
                                    {item.list.map((entry, e) => (
                                      <li
                                        key={e}
                                        className="flex items-center gap-[1rem]">
                                        <span
                                          className="h-[1.5rem] w-[1.5rem] shrink-0"
                                          style={{
                                            backgroundColor: accent,
                                            maskImage: `url(${flag.src})`,
                                            WebkitMaskImage: `url(${flag.src})`,
                                            maskSize: "100% 100%",
                                            WebkitMaskSize: "100% 100%",
                                          }}
                                          aria-hidden
                                        />
                                        <span className="text-[2.25rem] leading-[1.5] text-white [text-box-edge:cap_alphabetic] [text-box-trim:trim-both]">
                                          {entry}
                                        </span>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>

                {/* desktop: media of the open tab; the block above is sticky, so this rides along */}
                {current && (
                  <div className="relative hidden h-[83.25rem] w-[76.2rem] shrink-0 overflow-clip rounded-[.625rem] bg-[#101010] lg:block">
                    <AnimatePresence initial={false}>
                      <MotionDiv
                        key={active}
                        className="absolute inset-0"
                        initial={{ opacity: 0, scale: 1.04 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.6, ease: EASE }}>
                        <Media
                          item={current}
                          className="h-full w-full"
                        />
                      </MotionDiv>
                    </AnimatePresence>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {data?.methodeCTA && (
          <CTACornerButton
            className="mt-[7rem]"
            data={data.methodeCTA}
          />
        )}
      </div>
    </section>
  );
}
